/**
 * A meter — and the one that refuses to be a meter.
 *
 * `value === null` is **unmeasured**, and it is the reason this primitive exists
 * as a primitive rather than as a `<div>` with a width. Kimene's capacity type
 * is a pointer precisely so that "we have not measured this" is representable
 * and cannot be read as zero; a landing page that drew unknown as an empty bar
 * would be selling the opposite of the invariant on the same screen as the
 * sentence describing it.
 *
 * So unmeasured gets:
 *   - its own colour (`--k-unknown`), never the accent and never a grey that
 *     reads as "probably fine";
 *   - a hatch, so it is distinguishable without relying on hue;
 *   - the word, in the label, because colour alone is not a channel;
 *   - and **no animation, ever**. The rule is here rather than in each figure,
 *     so no figure can animate an unmeasured bar by forgetting not to.
 *
 * `role="meter"` is deliberately not used: a meter with no value is not a meter,
 * and an aria-valuenow of 0 would assert exactly the falsehood being avoided.
 */
import { m } from 'motion/react'

import { useInViewOnce } from './useInViewOnce'
import { useReducedMotionSafe } from './motion'

export type MeterTone = 'accent' | 'ok' | 'warn' | 'err' | 'info'

export function MeterFill({
  value,
  label,
  unknownLabel,
  tone = 'accent',
  delay = 0,
  className,
}: {
  /** 0–1, or `null` for unmeasured. There is no third state and no default. */
  value: number | null
  /** Human text for the row. Rendered by the caller; used here for a11y. */
  label: string
  /** What "unmeasured" is called in this language. Required when value can be null. */
  unknownLabel: string
  tone?: MeterTone
  delay?: number
  className?: string
}) {
  const reduced = useReducedMotionSafe()
  const [ref, seen] = useInViewOnce<HTMLDivElement>()

  if (value === null) {
    return (
      <div
        className={`kl-meter kl-meter--unknown ${className ?? ''}`}
        role="img"
        aria-label={`${label}: ${unknownLabel}`}
      >
        <div className="kl-meter__hatch" />
      </div>
    )
  }

  const pct = Math.max(0, Math.min(1, value)) * 100

  return (
    <div
      ref={ref}
      className={`kl-meter kl-meter--${tone} ${className ?? ''}`}
      role="meter"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      {reduced ? (
        <div className="kl-meter__fill" style={{ width: `${pct}%` }} />
      ) : (
        <m.div
          className="kl-meter__fill"
          initial={{ width: 0 }}
          animate={{ width: seen ? `${pct}%` : 0 }}
          transition={{ duration: 0.8, delay, ease: [0.32, 0.72, 0, 1] }}
        />
      )}
    </div>
  )
}
